import { useEffect, useSyncExternalStore, type ReactNode } from "react";

/**
 * (Task Q) The store app's bottom tab bar must never cover a primary action.
 *  - full-screen flows (Collect payment, the phone cart sheet, PIN entry, open / close business, the staff kiosk)
 *    call useHideTabBar() while they are open: the tab bar is not rendered and the page padding shrinks
 *  - every other page keeps padding-bottom = tab bar height + safe-area inset + 16 px (styles.css)
 *  - <ActionBar> pins primary buttons in a sticky bar ABOVE the tab bar (or above the safe area when it's hidden)
 */
let hiders = 0;
const listeners = new Set<() => void>();
const emit = () => {
  if (typeof document !== "undefined") {
    if (hiders > 0) document.documentElement.dataset.tabbar = "hidden";
    else delete document.documentElement.dataset.tabbar;
  }
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const tabBarHidden = () => hiders > 0;

/** Hide the bottom tab bar while this component is mounted (and active). */
export function useHideTabBar(active = true) {
  useEffect(() => {
    if (!active) return;
    hiders++; emit();
    return () => { hiders = Math.max(0, hiders - 1); emit(); };
  }, [active]);
}
export function useTabBarHidden(): boolean {
  return useSyncExternalStore(subscribe, tabBarHidden, tabBarHidden);
}

/** Sticky bar for a page's primary action(s), always above the tab bar and the safe area. */
export function ActionBar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={"action-bar" + (className ? " " + className : "")} data-testid="action-bar">{children}</div>;
}
